import { Layout } from "@/components/layout";
import { useEducation } from "@/lib/queries/use-education";
import { useExperience } from "@/lib/queries/use-experience";
import { useSiteContent } from "@/lib/queries/use-site-content";
import { useSocialLinks } from "@/lib/queries/use-social-links";

// Sharp arrow icon for external links
function ArrowUpRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="miter"
      strokeLinecap="square"
    >
      <path d="M3 9L9 3M9 3H4M9 3V8" />
    </svg>
  );
}

// Default bio HTML
const DEFAULT_BIO = `<p>I'm a first-generation immigrant and entrepreneur. I care deeply about using economics and engineering to build a future worth living in for all humankind.</p>
<p>In 2023, I co-founded Vista Power to enable every business to access affordable energy and protect their assets against power outages. Before that, I built internal tools for supply chain optimization at Rocket Lab and helped drive profit improvement at Starlink.</p>
<p>During my undergrad at UC Berkeley—where I graduated with degrees in Economics and Physics—I worked on connecting university students with the space industry through events and education via NewSpace@Berkeley. I taught an accredited course on NewSpace Entrepreneurship, and still informally advise current members on marketing, design, and recruitment.</p>
<p>In my spare time, I draw a lot, write sci-fi stories, spend time with people I care about, and work on different personal projects in areas I'm passionate about.</p>`;

export function About() {
  const { data: content } = useSiteContent();
  const { data: experience } = useExperience();
  const { data: education } = useEducation();
  const { data: socialLinks } = useSocialLinks();

  // Content with defaults
  const title = content?.about_title || "Maxim Kraft";
  const bio = content?.about_bio || DEFAULT_BIO;

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-8 pt-20 pb-16">
        {/* Header */}
        <p className="text-[11px] text-text-muted tracking-wide-caps uppercase mb-6">About</p>
        <h1 className="text-[44px] font-bold text-primary leading-[1.15] mb-8 tracking-tight">
          {title}
        </h1>

        {/* Bio */}
        <div className="max-w-2xl mb-16 prose-about" dangerouslySetInnerHTML={{ __html: bio }} />

        {/* Work History */}
        {experience && experience.length > 0 && (
          <div className="border-t border-border pt-8 mb-10">
            <h2 className="text-[11px] text-text-muted font-bold tracking-wide-caps uppercase mb-6">
              Experience
            </h2>

            <div className="space-y-6">
              {experience.map((job) => (
                <div key={job.id} className="grid grid-cols-[140px_1fr] gap-8">
                  <div>
                    <p className="text-[12px] text-text-faint tracking-wider">{job.period}</p>
                  </div>
                  <div>
                    {job.url ? (
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[16px] font-bold text-text-primary hover:text-primary transition-colors mb-1"
                      >
                        {job.company}
                        <ArrowUpRight className="size-3" />
                      </a>
                    ) : (
                      <p className="text-[16px] font-bold text-text-primary mb-1">{job.company}</p>
                    )}
                    <p className="text-[14px] text-text-tertiary mb-1">{job.role}</p>
                    {job.description && (
                      <div
                        className="text-[13px] text-text-muted [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mt-1.5 [&_li]:mb-1 [&_strong]:text-text-secondary [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2"
                        dangerouslySetInnerHTML={{ __html: job.description }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {education && education.length > 0 && (
          <div className="border-t border-border pt-8 mb-10">
            <h2 className="text-[11px] text-text-muted font-bold tracking-wide-caps uppercase mb-6">
              Education
            </h2>

            <div className="space-y-6">
              {education.map((edu) => (
                <div key={edu.id} className="grid grid-cols-[140px_1fr] gap-8">
                  <div>
                    <p className="text-[12px] text-text-faint tracking-wider">{edu.period}</p>
                  </div>
                  <div>
                    <p className="text-[16px] font-bold text-text-primary mb-1">
                      {edu.institution}
                    </p>
                    <p className="text-[14px] text-text-tertiary mb-1">{edu.degree}</p>
                    {edu.notes && <p className="text-[13px] text-text-muted">{edu.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact */}
        {socialLinks && socialLinks.length > 0 && (
          <div className="border-t border-border pt-8">
            <h2 className="text-[11px] text-text-muted font-bold tracking-wide-caps uppercase mb-6">
              Contact
            </h2>

            <div className="flex gap-8">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  target={link.url.startsWith("mailto:") ? undefined : "_blank"}
                  rel={link.url.startsWith("mailto:") ? undefined : "noopener noreferrer"}
                  className="inline-flex items-center gap-1 text-[14px] text-text-muted hover:text-primary transition-colors"
                >
                  {link.label}
                  <ArrowUpRight className="size-3" />
                </a>
              ))}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
